import styled from "@emotion/styled/macro";

const PRIMARY = "#283593";
const PRIMARY_LIGHT = "#3949ab";
const PRIMARY_BG = "#e8eaf6";
const SUCCESS = "#2e7d32";
const WARNING = "#f57f17";
const DANGER = "#c62828";
const INFO = "#0277bd";

/* ─── Page Wrapper ─── */
export const DRPageWrapper = styled("div")({
    padding: "16px 20px 32px",
    background: "#f5f6fa",
    minHeight: "calc(100vh - 120px)",
    "@media(max-width: 768px)": { padding: "10px 8px 24px" },
});

/* ─── Header ─── */
export const DRHeader = styled("div")({
    background: "linear-gradient(135deg, " + PRIMARY + " 0%, " + PRIMARY_LIGHT + " 100%)",
    borderRadius: "12px",
    padding: "20px 24px",
    marginBottom: "16px",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    "@media(max-width: 768px)": { padding: "14px 16px", flexDirection: "column", alignItems: "flex-start" },
});

export const DRHeaderLeft = styled("div")({ display: "flex", flexDirection: "column", gap: "4px" });
export const DRHeaderTitle = styled("h2")({ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#fff" });
export const DRHeaderSub = styled("p")({ margin: 0, fontSize: "0.85rem", opacity: 0.85 });

export const DRHeaderStats = styled("div")({
    display: "flex", gap: "10px", flexWrap: "wrap",
});

export const DRStatChip = styled("div")(function(props) {
    let bg = "rgba(255,255,255,0.15)";
    let border = "rgba(255,255,255,0.3)";
    if (props.variant === "success") { bg = "rgba(46,125,50,0.3)"; border = "rgba(46,125,50,0.5)"; }
    if (props.variant === "warning") { bg = "rgba(245,127,23,0.3)"; border = "rgba(245,127,23,0.5)"; }
    if (props.variant === "danger") { bg = "rgba(198,40,40,0.3)"; border = "rgba(198,40,40,0.5)"; }
    return {
        padding: "6px 14px",
        borderRadius: "20px",
        fontSize: "0.78rem",
        fontWeight: 600,
        background: bg,
        border: "1px solid " + border,
        color: "#fff",
        whiteSpace: "nowrap",
    };
});

export const DRStatusBadge = styled("span")(function(props) {
    let bg = PRIMARY_LIGHT;
    if (props.status === "saved") bg = SUCCESS;
    if (props.status === "draft") bg = WARNING;
    return {
        display: "inline-block",
        padding: "3px 12px",
        borderRadius: "12px",
        fontSize: "0.72rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        background: bg,
        color: "#fff",
    };
});

/* ─── Tabs ─── */
export const DRTabBar = styled("div")({
    display: "flex",
    gap: "4px",
    background: "#fff",
    borderRadius: "10px",
    padding: "4px",
    marginBottom: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflowX: "auto",
    "&::-webkit-scrollbar": { height: 0 },
});

export const DRTabBtn = styled("button")(function(props) {
    return {
        flex: "1 1 auto",
        padding: "10px 18px",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "0.82rem",
        fontWeight: props.active ? 700 : 500,
        background: props.active ? PRIMARY : "transparent",
        color: props.active ? "#fff" : "#555",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
        ":hover": { background: props.active ? PRIMARY : PRIMARY_BG },
    };
});

export const DRTabBadge = styled("span")({
    display: "inline-block",
    marginLeft: "6px",
    padding: "1px 7px",
    borderRadius: "10px",
    fontSize: "0.7rem",
    fontWeight: 700,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
});

/* ─── Alert Cards ─── */
export const DRAlertContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "16px",
});

export const DRAlertCard = styled("div")(function(props) {
    const colors = { critical: DANGER, warning: WARNING, info: INFO };
    const c = colors[props.severity] || INFO;
    return {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "12px 16px",
        borderRadius: "10px",
        background: c + "0a",
        border: "1px solid " + c + "30",
        borderLeft: "4px solid " + c,
        animation: "slideInDR 0.3s ease-out",
        "@keyframes slideInDR": {
            from: { opacity: 0, transform: "translateX(-10px)" },
            to: { opacity: 1, transform: "translateX(0)" },
        },
    };
});

export const DRAlertIcon = styled("span")({ fontSize: "1.3rem", lineHeight: 1.3 });
export const DRAlertContent = styled("div")({ flex: 1, minWidth: 0 });
export const DRAlertTitle = styled("div")({ fontWeight: 700, fontSize: "0.85rem", color: "#333", marginBottom: "2px" });
export const DRAlertDetail = styled("div")({ fontSize: "0.8rem", color: "#555" });
export const DRAlertAction = styled("div")({ fontSize: "0.75rem", color: "#777", marginTop: "4px", fontStyle: "italic" });

/* ─── Form Card ─── */
export const DRFormCard = styled("div")(function(props) {
    return {
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        marginBottom: "14px",
        borderTop: "3px solid " + (props.color || PRIMARY),
        overflow: "hidden",
    };
});

export const DRFormHeader = styled("div")(function(props) {
    return {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 18px",
        cursor: props.collapsible ? "pointer" : "default",
        background: props.collapsed ? "#fafafa" : "#fff",
        borderBottom: props.collapsed ? "none" : "1px solid #f0f0f0",
        transition: "background 0.2s",
        ":hover": props.collapsible ? { background: "#f5f7ff" } : {},
    };
});

export const DRFormHeaderLeft = styled("div")({ display: "flex", alignItems: "center", gap: "10px" });
export const DRFormIcon = styled("span")({ fontSize: "1.15rem" });
export const DRFormTitle = styled("h3")({ margin: 0, fontSize: "0.92rem", fontWeight: 700, color: "#333" });
export const DRFormBadge = styled("span")(function(props) {
    return {
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 600,
        background: props.variant === "success" ? "#e8f5e9" : props.variant === "warning" ? "#fff3e0" : "#e8eaf6",
        color: props.variant === "success" ? SUCCESS : props.variant === "warning" ? WARNING : PRIMARY,
    };
});

export const DRFormBody = styled("div")(function(props) {
    return {
        padding: props.collapsed ? "0" : "16px 18px",
        maxHeight: props.collapsed ? "0" : "2000px",
        overflow: "hidden",
        transition: "all 0.3s ease",
        opacity: props.collapsed ? 0 : 1,
    };
});

/* ─── Form Grid ─── */
export const DRFormGrid = styled("div")(function(props) {
    const cols = props.cols || 4;
    return {
        display: "grid",
        gridTemplateColumns: "repeat(" + cols + ", 1fr)",
        gap: "14px",
        "@media(max-width: 1024px)": { gridTemplateColumns: "repeat(3, 1fr)" },
        "@media(max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
        "@media(max-width: 480px)": { gridTemplateColumns: "1fr" },
    };
});

export const DRFormGroup = styled("div")(function(props) {
    return {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        gridColumn: props.span ? "span " + props.span : "auto",
        "@media(max-width: 768px)": { gridColumn: props.span > 2 ? "span 2" : "auto" },
        "@media(max-width: 480px)": { gridColumn: "span 1" },
    };
});

export const DRLabel = styled("label")(function(props) {
    return {
        fontSize: "0.78rem",
        fontWeight: 600,
        color: "#555",
        ":after": props.required ? { content: "' *'", color: DANGER } : {},
    };
});

export const DRHint = styled("span")(function(props) {
    return {
        fontSize: "0.72rem",
        color: props.warn ? DANGER : "#888",
        fontWeight: props.warn ? 600 : 400,
    };
});

/* ─── Checkbox / Toggle List ─── */
export const DRCheckboxGrid = styled("div")(function(props) {
    return {
        display: "grid",
        gridTemplateColumns: "repeat(" + (props.cols || 3) + ", 1fr)",
        gap: "8px",
        "@media(max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
        "@media(max-width: 480px)": { gridTemplateColumns: "1fr" },
    };
});

export const DRCheckboxItem = styled("label")(function(props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        borderRadius: "8px",
        border: "1px solid " + (props.checked ? PRIMARY : "#e0e0e0"),
        background: props.checked ? PRIMARY_BG : "#fff",
        cursor: "pointer",
        fontSize: "0.8rem",
        color: props.checked ? PRIMARY : "#555",
        fontWeight: props.checked ? 600 : 400,
        transition: "all 0.15s",
        ":hover": { borderColor: PRIMARY, background: PRIMARY_BG },
    };
});

/* ─── AMTSL Checklist ─── */
export const AMTSLCard = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    borderTop: "3px solid " + SUCCESS,
    marginBottom: "14px",
    overflow: "hidden",
});

export const AMTSLHeader = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 18px",
    borderBottom: "1px solid #f0f0f0",
});

export const AMTSLTitle = styled("h3")({ margin: 0, fontSize: "0.92rem", fontWeight: 700, color: "#333" });

export const AMTSLProgress = styled("div")(function(props) {
    return {
        padding: "2px 10px",
        borderRadius: "12px",
        fontSize: "0.75rem",
        fontWeight: 700,
        background: props.complete ? "#e8f5e9" : "#fff3e0",
        color: props.complete ? SUCCESS : WARNING,
    };
});

export const AMTSLBody = styled("div")({ padding: "14px 18px" });

export const AMTSLItem = styled("label")(function(props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 14px",
        borderRadius: "8px",
        marginBottom: "6px",
        background: props.checked ? "#e8f5e9" : "#fafafa",
        border: "1px solid " + (props.checked ? "#c8e6c9" : "#eee"),
        cursor: "pointer",
        transition: "all 0.15s",
        ":hover": { background: props.checked ? "#e8f5e9" : "#f5f5f5" },
    };
});

export const AMTSLCheckIcon = styled("span")(function(props) {
    return {
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.8rem",
        fontWeight: 700,
        background: props.checked ? SUCCESS : "#ddd",
        color: "#fff",
        transition: "background 0.2s",
    };
});

export const AMTSLLabel = styled("span")(function(props) {
    return {
        fontSize: "0.84rem",
        fontWeight: props.checked ? 600 : 400,
        color: props.checked ? "#333" : "#666",
        textDecoration: props.checked ? "none" : "none",
    };
});

/* ─── Summary Strip ─── */
export const DRSummaryStrip = styled("div")({
    display: "flex",
    gap: "12px",
    padding: "12px 16px",
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: "16px",
    overflowX: "auto",
    "@media(max-width: 768px)": { gap: "8px" },
});

export const DRSummaryItem = styled("div")(function(props) {
    return {
        flex: "1 0 auto",
        textAlign: "center",
        padding: "6px 12px",
        borderRadius: "8px",
        background: props.warn ? DANGER + "0d" : "#f8f9fa",
        minWidth: "80px",
    };
});

export const DRSummaryLabel = styled("div")({ fontSize: "0.7rem", color: "#888", fontWeight: 500, marginBottom: "2px" });
export const DRSummaryValue = styled("div")({ fontSize: "0.9rem", fontWeight: 700, color: "#333" });

/* ─── Buttons ─── */
export const DRPrimaryBtn = styled("button")(function(props) {
    return {
        padding: "10px 24px",
        borderRadius: "8px",
        border: "none",
        background: props.disabled ? "#bbb" : PRIMARY,
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.85rem",
        cursor: props.disabled ? "not-allowed" : "pointer",
        transition: "all 0.2s",
        ":hover": props.disabled ? {} : { background: PRIMARY_LIGHT, transform: "translateY(-1px)" },
    };
});

export const DRSecondaryBtn = styled("button")({
    padding: "10px 24px",
    borderRadius: "8px",
    border: "1px solid " + PRIMARY,
    background: "#fff",
    color: PRIMARY,
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    transition: "all 0.2s",
    ":hover": { background: PRIMARY_BG },
});

export const DRDangerBtn = styled("button")({
    padding: "10px 24px",
    borderRadius: "8px",
    border: "none",
    background: DANGER,
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    ":hover": { background: "#b71c1c" },
});

export const DRSuccessBtn = styled("button")({
    padding: "10px 24px",
    borderRadius: "8px",
    border: "none",
    background: SUCCESS,
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    ":hover": { background: "#1b5e20" },
});

export const DRAdmissionBtn = styled("button")({
    padding: "10px 24px",
    borderRadius: "8px",
    border: "none",
    background: "#004d40",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.85rem",
    cursor: "pointer",
    transition: "all 0.2s",
    ":hover": { background: "#00695c" },
});

/* ─── PPH Alert Box ─── */
export const PPHAlertBox = styled("div")(function(props) {
    const c = props.severity === "critical" ? DANGER : WARNING;
    return {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "14px 18px",
        borderRadius: "10px",
        background: c + "12",
        border: "2px solid " + c + "40",
        marginTop: "8px",
        animation: "pulseBorder 1.5s infinite",
        "@keyframes pulseBorder": {
            "0%, 100%": { borderColor: c + "40" },
            "50%": { borderColor: c },
        },
    };
});

export const PPHIcon = styled("span")({ fontSize: "1.5rem" });
export const PPHContent = styled("div")({ flex: 1 });
export const PPHTitle = styled("div")(function(props) {
    const c = props.severity === "critical" ? DANGER : WARNING;
    return { fontWeight: 700, fontSize: "0.92rem", color: c };
});
export const PPHDetail = styled("div")({ fontSize: "0.8rem", color: "#555", marginTop: "2px" });
export const PPHAction = styled("div")({ fontSize: "0.78rem", color: "#333", fontWeight: 600, marginTop: "4px" });

/* ─── Section Navigation (within form) ─── */
export const DRSectionNav = styled("div")({
    display: "flex",
    gap: "6px",
    marginBottom: "12px",
    overflowX: "auto",
    paddingBottom: "4px",
    "&::-webkit-scrollbar": { height: "3px" },
    "&::-webkit-scrollbar-thumb": { background: "#ccc", borderRadius: "3px" },
});

export const DRSectionBtn = styled("button")(function(props) {
    return {
        padding: "6px 14px",
        borderRadius: "16px",
        border: "1px solid " + (props.active ? PRIMARY : "#e0e0e0"),
        background: props.active ? PRIMARY_BG : "#fff",
        color: props.active ? PRIMARY : "#666",
        fontWeight: props.active ? 600 : 500,
        fontSize: "0.78rem",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "all 0.15s",
        ":hover": { borderColor: PRIMARY, background: PRIMARY_BG },
    };
});

/* ─── Loading / Empty States ─── */
export const DRLoading = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 20px",
    color: "#888",
    fontSize: "0.9rem",
});

export const DRNoAdmission = styled("div")({
    textAlign: "center",
    padding: "60px 20px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
});

export const DRNoAdmissionIcon = styled("div")({ fontSize: "3rem", marginBottom: "12px" });
export const DRNoAdmissionTitle = styled("h3")({ margin: "0 0 8px", color: "#333", fontSize: "1.1rem" });
export const DRNoAdmissionDesc = styled("p")({ margin: "0 0 16px", color: "#777", fontSize: "0.85rem" });

/* ─── Button Row ─── */
export const DRButtonRow = styled("div")({
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "16px 0 0",
    flexWrap: "wrap",
});

/* ─── Saved Record Display ─── */
export const DRRecordCard = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    border: "1px solid #e8eaf6",
    padding: "16px 20px",
    marginBottom: "14px",
});

export const DRRecordRow = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: "1px solid #f5f5f5",
    ":last-child": { borderBottom: "none" },
});

export const DRRecordLabel = styled("span")({ fontSize: "0.8rem", color: "#888", fontWeight: 500 });
export const DRRecordValue = styled("span")(function(props) {
    return {
        fontSize: "0.85rem",
        fontWeight: 600,
        color: props.warn ? DANGER : "#333",
    };
});
